'use client'

import { useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Attachment01Icon, Cancel01Icon, File01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useWorkspace } from '@/contexts/WorkspaceContext'

type RunAttachment = {
  id: string
  file_name: string
  file_type: string
  file_size: number
}

export function CommandRunAttachments({
  attachments,
  onChange,
}: {
  attachments: RunAttachment[]
  onChange: (attachments: RunAttachment[]) => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { workspaceId } = useWorkspace()
  const [uploading, setUploading] = useState(false)

  const uploadFile = async (file: File) => {
    if (!workspaceId) {
      toast.error('No workspace selected')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('workspaceId', workspaceId)
      const response = await fetch('/api/chat/upload', { method: 'POST', body: formData })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        toast.error(typeof data.error === 'string' ? data.error : 'Upload failed')
        return
      }
      const attachment = data as RunAttachment
      onChange([...attachments, attachment])
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void uploadFile(file)
        }}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="gap-2"
      >
        <HugeiconsIcon icon={Attachment01Icon} className="size-4" />
        {uploading ? 'Uploading...' : 'Attach files'}
      </Button>

      {attachments.length > 0 ? (
        <div className="space-y-1">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex min-w-0 items-center gap-2 rounded-md border border-atrium-border bg-white px-2 py-1.5 text-sm text-atrium-black"
            >
              <HugeiconsIcon icon={File01Icon} className="size-4 shrink-0 text-atrium-black/55" />
              <span className="min-w-0 flex-1 truncate">{attachment.file_name}</span>
              <button
                type="button"
                className="rounded p-1 text-atrium-black/50 hover:bg-atrium-content hover:text-atrium-black"
                onClick={() => onChange(attachments.filter((item) => item.id !== attachment.id))}
                aria-label={`Remove ${attachment.file_name}`}
              >
                <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
